import React from 'react'
import styled from 'styled-components'
import { Box, Flex, Text } from '../../../core-components'
import { validatePositioningProps } from '../../../helpers/validatePositioningProps'
import { PositioningProps } from '../../types'
import { Tooltip } from '../tooltip/Tooltip'
import { ThemeColors } from '../../../theme'

type Variant = 'neutral' | 'warning' | 'inform' | 'success' | 'caution'
type Props = {
  'data-test-id'?: string
  label: string
  size?: 'default' | 'compact'
  variant: Variant
  tooltip?: string
} & PositioningProps

export const Badge = ({
  'data-test-id': testId,
  label,
  variant,
  size = 'default',
  tooltip,
  ...positioningProps
}: Props) => {
  validatePositioningProps(positioningProps, 'Badge')

  if (size === 'compact') {
    return (
      <Tooltip content={tooltip ?? label}>
        <Tooltip.NonFocusableTrigger>
          <StyledCompactBadge
            {...positioningProps}
            data-test-id={testId}
            variant={variant}
            alignItems='center'
            justifyContent='center'
            borderRadius={8}
            w={4}
            h={4}
          >
            <StyledCompactBadgeCore w='6px' h='6px' borderRadius={6} />
          </StyledCompactBadge>
        </Tooltip.NonFocusableTrigger>
      </Tooltip>
    )
  }

  if (tooltip) {
    return (
      <Tooltip content={tooltip}>
        <Tooltip.NonFocusableTrigger>
          <StyledBadge
            {...positioningProps}
            as='div'
            data-test-id={testId}
            py='3px'
            px={2}
            textStyle='heading-2xs'
            color={textColors[variant]}
            variant={variant}
          >
            {label}
          </StyledBadge>
        </Tooltip.NonFocusableTrigger>
      </Tooltip>
    )
  }

  return (
    <StyledBadge
      {...positioningProps}
      as='div'
      data-test-id={testId}
      py='3px'
      px={2}
      textStyle='heading-2xs'
      color={textColors[variant]}
      variant={variant}
    >
      {label}
    </StyledBadge>
  )
}

const textColors = {
  neutral: 'fg/moderate',
  warning: 'fg/warning',
  caution: 'fg/caution',
  inform: 'fg/inform',
  success: 'fg/success',
} satisfies Record<Variant, ThemeColors>

const bgColors = {
  neutral: 'bg/major',
  warning: 'bg/warning',
  caution: 'bg/caution',
  inform: 'bg/inform',
  success: 'bg/success',
} satisfies Record<Variant, ThemeColors>

const bgCoreColors = {
  neutral: 'fg/minor',
  warning: 'fg/warning',
  caution: 'fg/caution',
  inform: 'fg/inform',
  success: 'fg/success',
} satisfies Record<Variant, ThemeColors>

const StyledBadge = styled(Text)<{ variant: Variant }>`
  border-radius: 24px;
  background: ${({ theme, variant }) => theme.colors[bgColors[variant]]};
  white-space: nowrap;
`

const StyledCompactBadgeCore = styled(Box)``
const StyledCompactBadge = styled(Flex)<{ variant: Variant }>`
  background: ${({ theme, variant }) => theme.colors[bgColors[variant]]};
  ${StyledCompactBadgeCore} {
    background: ${({ theme, variant }) => theme.colors[bgCoreColors[variant]]};
  }

  :hover {
    background: ${({ theme, variant }) => theme.colors[bgCoreColors[variant]]};
    ${StyledCompactBadgeCore} {
      background: ${({ theme }) => theme.colors['fg/major-on-color']};
    }
  }
`
